/**
 * Request Signing Utility for Frontend
 *
 * Client-side utilities for generating request signatures to ensure
 * integrity verification for file uploads.
 *
 * @author Karl Groves <karl.groves@afixt.com>
 */

/**
 * Generate a simple hash for client-side signing
 * Note: This is not cryptographically secure but provides basic integrity checking
 */
async function generateSimpleHash(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = [...new Uint8Array(hashBuffer)];
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate request nonce
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return [...array].map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate file upload signature data
 * @param {File} file - File to be uploaded
 * @param {string} userId - Current user ID
 * @returns {Promise<string>} Base64 encoded signature data
 */
export async function generateFileUploadSignature(file: File, userId: string): Promise<string> {
  const timestamp = Date.now();
  const nonce = generateNonce();

  const signatureData = {
    filename: file.name,
    size: file.size,
    mimetype: file.type,
    timestamp,
    userId,
    nonce,
    // Add a simple client-side checksum for basic integrity
    checksum: await generateSimpleHash(
      `${file.name}${file.size}${file.type}${timestamp}${userId}${nonce}`
    ),
  };

  // Note: For production use, this should include a proper HMAC signature
  // generated server-side or using a shared secret in a secure way
  const signature = await generateSimpleHash(JSON.stringify(signatureData));

  const signedData = {
    ...signatureData,
    signature,
  };

  // Encode as base64 for transport
  return btoa(JSON.stringify(signedData));
}

/**
 * Check if request signing is supported in current environment
 */
export function isRequestSigningSupported(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    crypto.subtle !== undefined &&
    crypto.getRandomValues !== undefined
  );
}
